import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ContentConstants from '../contentConstants';
import * as SurveyPrivacyPolicyAPI from '../services/SurveyPrivacyPolicyAPI';
import { ActionTypes, ActionTypes as CommonActionTypes } from './ActionTypes';
import { IApplicationState } from '.';
import { EditState, ISurveyPrivacyPolicyEditModel, ITranslationEditItem } from '../models';
import Constants from '../constants';
import { ITranslationApiParameters, saveTranslations } from '../services/TranslationsAPI';
import _ from 'lodash';
import { ActionCreators } from '../store/TranslationEditorStore';

export interface ISurveyPrivacyPolicyState {
	isLoading: boolean;
	isClearOrCopyInProgress?: boolean;
	isError: boolean;
}

export const initialState: ISurveyPrivacyPolicyState = {
	isLoading: false,
	isClearOrCopyInProgress: false,
	isError: false,
};

export const savePrivacyPolicyAdminData = createAsyncThunk(
	'SurveyPrivacyPolicy/savePrivacyPolicyAdminData',
	async (surveyId: number, { dispatch, getState, rejectWithValue }) => {
		try {
			const state = getState() as IApplicationState;
			const surveyEditModel = state.survey.surveyEditModel;
			const surveyPrivacyPolicyEditModel: ISurveyPrivacyPolicyEditModel = {
				...surveyEditModel.privacyPolicy,
			};
			const updatedPrivacyPolicy = await SurveyPrivacyPolicyAPI.UpdatePrivacyPolicy(surveyId, surveyPrivacyPolicyEditModel);

			const privacyPolicyKey = 'PrivacyPolicy';
			const titleKey = 'Title';
			const contentKey = 'Content';
			const checkboxTextKey = 'CheckBoxText';
			const baseApi = Constants.API_TRANSLATIONS_URL + '/' + surveyId + '/' + privacyPolicyKey;
			const translationsToBeSaved: { [translationKey: string]: ITranslationEditItem[] } = {};
			// Set the edit state of default culture translations to Update so the translations update is triggered
			// The way the translation are update on Survey Admin is different from
			// the way it is updated on Survey content page before publishing, hence we need to update the default language translations explicitly
			// NOTE: once the non-english default language work is merged to Dev, the below code needs to be updated to refer to default culture of survey
			if (surveyEditModel.privacyPolicy.surveyPrivacyPolicyId) {
				const translationInstances = _.cloneDeep(state.translations.instances);
				if (translationInstances) {
					// Title
					const titleTranslations = translationInstances[`${privacyPolicyKey}/${titleKey}`]?.translations;
					if (titleTranslations) {
						const defaultTitleTranslation = _.find(
							titleTranslations,
							(i: ITranslationEditItem) => i.cultureCode === Constants.DEFAULT_CULTURE_CODE,
						);
						if (defaultTitleTranslation) {
							defaultTitleTranslation.textValue = surveyEditModel.privacyPolicy?.title?.textValue;
							defaultTitleTranslation.editState = defaultTitleTranslation.textValue ? EditState.Update : EditState.Remove;
						}
						ActionCreators.updateTranslations(`${privacyPolicyKey}/${titleKey}`, titleTranslations);

						translationsToBeSaved[titleKey] = titleTranslations;
					}

					//Content
					const contentTranslations = translationInstances[`${privacyPolicyKey}/${contentKey}`]?.translations;
					if (contentTranslations) {
						const defaultContentTranslation = _.find(
							contentTranslations,
							(i: ITranslationEditItem) => i.cultureCode === Constants.DEFAULT_CULTURE_CODE,
						);
						if (defaultContentTranslation) {
							defaultContentTranslation.textValue = surveyEditModel.privacyPolicy?.content?.textValue;
							defaultContentTranslation.editState = defaultContentTranslation.textValue ? EditState.Update : EditState.Remove;
						}

						ActionCreators.updateTranslations(`${privacyPolicyKey}/${contentKey}`, contentTranslations);

						translationsToBeSaved[contentKey] = contentTranslations;
					}

					//Checkbox Text
					const checkboxTextTranslations = translationInstances[`${privacyPolicyKey}/${checkboxTextKey}`]?.translations;
					if (checkboxTextTranslations) {
						const defaultCheckBoxTextTranslation = _.find(
							checkboxTextTranslations,
							(i: ITranslationEditItem) => i.cultureCode === Constants.DEFAULT_CULTURE_CODE,
						);
						if (defaultCheckBoxTextTranslation) {
							defaultCheckBoxTextTranslation.textValue = surveyEditModel.privacyPolicy?.checkboxText?.textValue;
							defaultCheckBoxTextTranslation.editState = defaultCheckBoxTextTranslation.textValue ? EditState.Update : EditState.Remove;
						}

						ActionCreators.updateTranslations(`${privacyPolicyKey}/${checkboxTextKey}`, checkboxTextTranslations);

						translationsToBeSaved[checkboxTextKey] = checkboxTextTranslations;
					}

					saveSurveyPrivacyPolicyTranslations(
						baseApi,
						surveyEditModel.privacyPolicy.surveyPrivacyPolicyId,
						surveyEditModel.defaultCultureCode,
						translationsToBeSaved,
					);
				}
			}
			// Fix below
			dispatch({ type: ActionTypes.SURVEY_PRIVACYPOLICY_ADMIN_SAVED, payload: updatedPrivacyPolicy });
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const copyPrivacyPolicy = createAsyncThunk(
	'SurveyPrivacyPolicy/copyPrivacyPolicy',
	async (surveyId: number, { dispatch, rejectWithValue }) => {
		try {
			const surveyPrivacyPolicy = await SurveyPrivacyPolicyAPI.copyPrivacyPolicy(surveyId);
			dispatch({ type: CommonActionTypes.SURVEY_PRIVACYPOLICY_COPY_UPDATE, payload: surveyPrivacyPolicy });
			return { surveyPrivacyPolicy };
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_SURVEY_PRIVACY_POLICY_COPY_FAILED });
			rejectWithValue(error);
		}
	},
);

export const clearPrivacyPolicy = createAsyncThunk(
	'SurveyPrivacyPolicy/clearPrivacyPolicy',
	async (surveyId: number, { dispatch, rejectWithValue }) => {
		try {
			const surveyPrivacyPolicy = await SurveyPrivacyPolicyAPI.clearPrivacyPolicy(surveyId);
			dispatch({ type: CommonActionTypes.SURVEY_PRIVACYPOLICY_CLEAR_UPDATE, payload: surveyPrivacyPolicy });
			return { surveyPrivacyPolicy };
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_SURVEY_PRIVACY_POLICY_CLEAR_FAILED });
			rejectWithValue(error);
		}
	},
);

async function saveSurveyPrivacyPolicyTranslations(
	baseApiUrl: string,
	uniqueId: number,
	defaultCultureCode: string,
	translationsToBeSaved: { [translationKey: string]: ITranslationEditItem[] },
) {
	if (translationsToBeSaved && Object.keys(translationsToBeSaved).length > 0) {
		for (const [translationKey, translations] of Object.entries(translationsToBeSaved)) {
			// if no changes, don't call the API
			if (_.some(translations, (i: ITranslationEditItem) => i.editState !== EditState.None)) {
				const apiParams: ITranslationApiParameters = {
					apiUrl: baseApiUrl + '/' + translationKey,
					uniqueId,
				};
				await saveTranslations(apiParams, defaultCultureCode, translations);
			}
		}
	}
}

const SurveyPrivacyPolicySlice = createSlice({
	name: 'SurveyPrivacyPolicy',
	initialState: initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(copyPrivacyPolicy.pending, (state) => {
				state.isClearOrCopyInProgress = true;
			})
			.addCase(copyPrivacyPolicy.fulfilled, (state) => {
				state.isClearOrCopyInProgress = false;
			})
			.addCase(copyPrivacyPolicy.rejected, (state) => {
				state.isClearOrCopyInProgress = false;
			})
			.addCase(clearPrivacyPolicy.pending, (state) => {
				state.isClearOrCopyInProgress = true;
			})
			.addCase(clearPrivacyPolicy.fulfilled, (state) => {
				state.isClearOrCopyInProgress = false;
			})
			.addCase(clearPrivacyPolicy.rejected, (state) => {
				state.isClearOrCopyInProgress = false;
			})
			.addCase(savePrivacyPolicyAdminData.pending, (state) => {
				state.isLoading = true;
				state.isError = false;
			})
			.addCase(savePrivacyPolicyAdminData.fulfilled, (state) => {
				state.isLoading = false;
				state.isError = false;
			})
			.addCase(savePrivacyPolicyAdminData.rejected, (state) => {
				state.isLoading = false;
				state.isError = true;
			});
	},
});

export default SurveyPrivacyPolicySlice.reducer;
